/**
 * Header 스타일
 * @description 시안 `3.0/shell/header`. 탭 화면은 8, 그 밖은 16 이 정본이다 — 시안의 탭 화면
 *   16 은 디자이너 오반영이다 (PRD `tab-screens-new-ds` §3.1).
 *
 *   ghost IconButton 은 스스로 12 여백을 갖는다. 껍데기가 `16 - 12 = 4` 만 주면 글리프가
 *   화면 여백 16 에 맞는다. ghost 가 아닌 것에는 보정하지 않는다. 탭 화면(8)에서는
 *   보정하지 않는다 — 8 은 이미 IconButton 여백보다 작다.
 */
import { cva } from "class-variance-authority";

export const headerStyles = {
  /*
   * 웹에서는 위에 붙어 있는다 — 앱은 화면마다 헤더가 있어 따라올 필요가 없지만, 긴 문서를
   * 스크롤하는 웹에서 이름과 공유·홈이 함께 사라지면 돌아갈 길이 없다. 바탕은 살짝 비쳐서
   * 밑으로 지나가는 내용이 흐리게 보인다(`backdrop-blur`). 높이는 `--fw-header-height`.
   */
  root: cva(
    "relative sticky top-0 z-30 flex w-full items-center gap-[var(--fw-space-gap-md)] py-[var(--fw-space-inset-sm)] bg-[var(--fw-color-surface-neutral-primary)]/90 backdrop-blur-md",
    {
      variants: {
        inset: {
          tab: "px-[var(--fw-inset-tab)]",
          screen: "px-[var(--fw-inset-screen)]",
        },
        hasBack: { true: "", false: "" },
        trailingGhost: { true: "", false: "" },
      },
      compoundVariants: [
        {
          inset: "screen",
          hasBack: true,
          class: "pl-[var(--fw-space-inset-xs)]",
        },
        {
          inset: "screen",
          trailingGhost: true,
          class: "pr-[var(--fw-space-inset-xs)]",
        },
      ],
      defaultVariants: {
        inset: "screen",
        hasBack: false,
        trailingGhost: false,
      },
    },
  ),
  /** 제목과 그에 딸린 것. 남는 자리를 모두 먹어 오른쪽 슬롯을 끝으로 민다. 위쪽에 맞춘다 */
  lead: "flex min-w-0 flex-1 items-start gap-[var(--fw-space-gap-xxs)]",
  title:
    "fw-h2 min-w-0 truncate text-[var(--fw-color-content-neutral-secondary)]",
  /** 제목에 딸린 개수 — 위쪽 정렬과 짝이 되어 제목의 어깨에 올라앉는다 */
  count: "fw-caption text-[var(--fw-color-content-neutral-tertiary)]",
  /** 오른쪽 슬롯 — 버튼이 둘 이상 설 수 있어 스스로 줄을 이룬다 */
  following: "flex items-center",
};

/**
 * 브랜드 슬롯. 심볼과 워드마크가 한 덩어리로 박힌 3.0 락업 그림(`images/3.0`)을 쓴다 —
 * 글자로 조판하던 때와 달리 자간·굵기가 시안 그대로 나온다.
 */
export const headerBrandSlotStyles = {
  root: "flex h-[var(--fw-size-control-lg)] min-w-0 flex-1 items-center",
  logo: "flex items-center rounded-[var(--fw-radius-sm)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fw-color-border-focused)]",
  /** 높이만 잠그고 폭은 그림의 비율(775×187)에 맡긴다 */
  lockup: "w-auto",
};

/**
 * 락업의 표시 크기. 원본 775×187 의 비율을 지킨 값이라 폭은 높이를 따라온다.
 * 높이 28 은 앞서 심볼이 서 있던 크기 그대로다 — 슬롯(48) 안에서 워드마크가 `fw-h3`(20) 와
 * 같은 크기로 읽힌다.
 */
export const HEADER_BRAND_LOCKUP = { width: 116, height: 28 } as const;
